import {
  getV1MessagingChannelsRecipientsIds,
  GetV1MessagingChannelsRecipientsTypeaheadData,
  MessagingChannel_Recipient_Read,
} from '@treadinc/horizon-api-spec';

import { MessagingRecipient } from '~hooks/useMessaging/models';
import { extractPagination } from '~services/pagination';
import { LabeledOption } from '~types/LabeledOption';
import fetchAllPages from '~utils/fetchAllPages';

type MessagingRecipientFilters = {
  companyIds: string[];
  labelIds: string[];
  scopedCompanyId: string;
};

export type RecipientsQuery = NonNullable<
  GetV1MessagingChannelsRecipientsTypeaheadData['query']
>;

export function messagingRecipientsQuery({
  companyIds,
  labelIds,
  scopedCompanyId,
}: MessagingRecipientFilters): RecipientsQuery {
  return {
    scoped_company_id: scopedCompanyId,
    ...(companyIds.length > 0 ? { 'filter[company_ids]': companyIds } : {}),
    ...(labelIds.length > 0 ? { 'filter[label_ids]': labelIds } : {}),
  };
}

/** The api refuses a broadcast above this many recipients. */
export const MAX_BROADCAST_RECIPIENTS = 500;

/**
 * Adds the matches the selection lacks, never more than the cap allows, so select all keeps the
 * drivers picked by hand. A filter change still clears them, so the selection matches the filters.
 */
export function mergeMatchingRecipients(
  selected: LabeledOption[],
  matching: LabeledOption[],
  hasMoreMatching: boolean,
) {
  const selectedIds = new Set(selected.map((recipient) => recipient.value));
  const additions = matching.filter((recipient) => !selectedIds.has(recipient.value));
  const room = Math.max(MAX_BROADCAST_RECIPIENTS - selected.length, 0);
  const recipients = [...selected, ...additions.slice(0, room)];
  const leftOutMatches = hasMoreMatching || additions.length > room;

  return {
    isCapped: leftOutMatches && recipients.length === MAX_BROADCAST_RECIPIENTS,
    recipients,
  };
}

export async function fetchMatchingRecipients(
  filters: MessagingRecipientFilters & { search: string },
  signal?: AbortSignal,
) {
  const matches: MessagingChannel_Recipient_Read[] = [];

  // One match past the cap is enough to know the selection is capped.
  await fetchAllPages(
    async (cursor, limit, pageSignal) => {
      const response = await getV1MessagingChannelsRecipientsIds({
        query: {
          ...messagingRecipientsQuery(filters),
          ...(filters.search ? { 'search[query]': filters.search } : {}),
          'page[limit]': limit,
          ...(cursor ? { 'page[after]': cursor } : {}),
        },
        signal: pageSignal,
      });

      matches.push(...response.data.data);
      const isPastCap = matches.length > MAX_BROADCAST_RECIPIENTS;

      return {
        data: response.data.data,
        pagination: isPastCap ? undefined : extractPagination(response),
      };
    },
    { signal },
  );

  return {
    hasMore: matches.length > MAX_BROADCAST_RECIPIENTS,
    recipients: matches
      .slice(0, MAX_BROADCAST_RECIPIENTS)
      .map(MessagingRecipient.parse)
      .map((recipient): LabeledOption => ({
        label: recipient.fullName,
        value: recipient.id,
      })),
  };
}
